import React from 'react';
import { Link } from 'react-router-dom';
import platformImage from '../assests/main image.png';

const marketingWorkflows = [
  'Promotional campaigns', 'Product announcements', 'New customer engagement',
  'Lead follow-ups', 'Abandoned-cart communication', 'Offers and promotions',
  'Event invitations', 'Appointment reminders', 'Order and delivery updates',
  'Customer re-engagement', 'Feedback requests', 'Personalized notifications',
];
const apiFeatures = [
  ['Send WhatsApp Messages', 'Send supported WhatsApp messages through application workflows.'],
  ['Approved Templates', 'Use approved message templates where required.'],
  ['Media', 'Integrate supported media into relevant customer communication.'],
  ['Conversations', 'Connect customer conversations to your business tools.'],
  ['Webhooks', 'Receive supported events and process them in your application.'],
  ['Message Status', 'Track available message status events.'],
  ['Automation', 'Build workflows triggered by application events.'],
  ['System Integrations', 'Connect WhatsApp with existing business applications.'],
];
const personalizationFields = [
  'Customer name', 'Product information', 'Order information', 'Appointment details',
  'Offer information', 'Account information', 'Custom application data',
];
const templateUseCases = [
  'Marketing campaigns', 'Promotions', 'Customer notifications',
  'Appointment reminders', 'Order updates', 'Account notifications',
  'Authentication messages',
];
const mediaTypes = ['Images', 'Documents', 'Videos', 'Audio', 'Other supported WhatsApp media'];
const ecommerceJourneys = [
  'New product announcements', 'Product promotions', 'Order updates',
  'Delivery notifications', 'Customer support', 'Customer re-engagement',
  'Promotional campaigns', 'Post-purchase communication',
];
const engagementSegments = [
  ['New Customers', 'Welcome customers and share useful information after signup or purchase.'],
  ['Existing Customers', 'Send relevant updates, offers, product information, and service communication.'],
  ['Returning Customers', 'Create re-engagement workflows based on your application data.'],
  ['Leads', 'Follow up with prospects and continue conversations through WhatsApp.'],
];
const saasFeatures = [
  'Customer notifications', 'Marketing workflows', 'Automated messaging',
  'Templates', 'Webhooks', 'Media', 'Customer communication', 'Multi-client workflows',
];
const agencyUseCases = [
  'Customer engagement', 'Campaign messaging', 'Lead follow-up',
  'Marketing automation', 'CRM integrations', 'Client-specific workflows',
  'Reporting and analytics',
];
const applicationIntegrations = [
  'CRM systems', 'E-commerce platforms', 'Websites', 'Mobile applications',
  'SaaS applications', 'Customer support systems', 'Lead management systems',
  'Marketing platforms', 'Internal business applications', 'Custom software',
];
const developerCapabilities = [
  'Messages', 'Templates', 'Media', 'Webhooks', 'Phone numbers',
  'WhatsApp Business Accounts', 'Business profiles', 'Flows', 'QR codes',
  'Analytics', 'Other supported API capabilities',
];
const analyticsMetrics = ['Messages sent', 'Message delivery', 'Message status', 'Customer interactions', 'Campaign activity', 'Workflow performance'];
const journeyStages = [
  ['Acquire', 'Connect leads and prospects with your business.'],
  ['Engage', 'Start relevant conversations and provide useful information.'],
  ['Convert', 'Help customers move from interest to action.'],
  ['Retain', 'Keep customers informed and connected.'],
  ['Support', 'Connect customer conversations with business systems.'],
];
const platformBenefits = [
  ['Integrate With Your Systems', 'Connect WhatsApp with the tools your business already uses.'],
  ['Automate Communication', 'Reduce repetitive manual messaging through workflows.'],
  ['Personalize Experiences', 'Use application data to create relevant communication.'],
  ['Scale Messaging', 'Build communication workflows that can grow with your business.'],
  ['Developer-Friendly', 'Use APIs and webhooks to create custom WhatsApp experiences.'],
  ['Centralize Workflows', 'Connect WhatsApp with CRM, SaaS, ecommerce, and internal systems.'],
];
const useCases = [
  ['Lead Generation', 'Follow up with leads and continue conversations through WhatsApp.'],
  ['Promotions', 'Deliver relevant promotional communication using approved templates and customer data.'],
  ['Product Updates', 'Announce new products, services, features, and other updates.'],
  ['Customer Retention', 'Create re-engagement workflows based on customer activity.'],
  ['E-commerce', 'Connect product, order, delivery, and customer workflows.'],
  ['Events', 'Send invitations, reminders, updates, and useful event information.'],
  ['Appointments', 'Connect booking workflows with notifications and reminders.'],
  ['Customer Support', 'Connect incoming WhatsApp communication to support or CRM workflows.'],
];
const faqs = [
  ['What is WhatsApp marketing?', 'WhatsApp marketing is the use of WhatsApp for promotional, informational, transactional, and engagement-focused communication, subject to applicable policies and messaging requirements.'],
  ['What is a WhatsApp Marketing API?', 'A WhatsApp Marketing API lets applications and business systems integrate WhatsApp messaging into their workflows programmatically.'],
  ['Can I integrate WhatsApp marketing with my CRM?', 'Yes. API-based integrations can connect WhatsApp messaging with CRM customer information, conversations, events, and workflows.'],
  ['Can I automate WhatsApp marketing?', 'Yes. Businesses can build workflows that trigger WhatsApp communication from events or application data.'],
  ['Can I send personalized WhatsApp messages?', 'Where supported by the applicable messaging rules and message type, an application can populate approved template variables with relevant customer information.'],
  ['Can I send images and other media?', 'WhatsApp supports several media formats. Use media types supported by the relevant API messaging capability.'],
  ['Is WhatsApp marketing suitable for SaaS products?', 'Yes. SaaS platforms can integrate WhatsApp capabilities and offer messaging as part of their customer workflows.'],
  ['Can agencies use WhatsApp API for multiple clients?', 'Agencies can build client-specific workflows using appropriate WhatsApp Business Accounts, phone numbers, permissions, and API architecture.'],
  ['Does WhatsApp marketing require message templates?', 'Business-initiated messaging may require approved templates depending on conversation context and applicable WhatsApp rules.'],
  ['How do I integrate WhatsApp marketing with my application?', 'Review the HAWKTRION API documentation, configure required WhatsApp Business resources, authenticate your application, implement relevant messaging operations, and configure webhooks.'],
];


const SectionHeading = ({ eyebrow, title, children }) => (
  <div className="business-api-heading">
    {eyebrow && <div className="sec-label"><span className="sec-label-dot"></span>{eyebrow}</div>}
    <h2>{title}</h2>
    {children && <p>{children}</p>}
  </div>
);

const BulletList = ({ items }) => (
  <ul className="business-api-list">{items.map((item) => <li key={item}>{item}</li>)}</ul>
);

const CardGrid = ({ items }) => (
  <div className="business-api-grid">
    {items.map(([title, description]) => <article className="business-api-card" key={title}><h3>{title}</h3><p>{description}</p></article>)}
  </div>
);

const Workflow = ({ steps }) => (
  <ol className="business-api-workflow">
    {steps.map((step, index) => <li key={step}><span className="business-api-step-number">{index + 1}</span><span>{step}</span></li>)}
  </ol>
);

const WhatsappMarketingContent = () => (
  <div className="business-api-content marketing-api-content">
    <section className="business-api-section business-api-intro">
      <div className="container">
        <div className="business-api-intro-grid">
          <div className="business-api-intro-copy">
            <p className="business-api-lead">Reach customers where they already chat.</p>
            <p>Build personalized WhatsApp marketing campaigns with HAWKTRION. Connect your business to the WhatsApp Business API and create scalable customer engagement workflows for promotions, product updates, lead follow-ups, reminders, offers, and more.</p>
            <p>HAWKTRION gives businesses, SaaS platforms, agencies, and developers tools to integrate WhatsApp messaging into existing workflows and applications.</p>
            <div className="business-api-actions">
              <Link to="/signup" className="btn btn-p">Get Started</Link>
              <Link to="/whatsapp-api-documentation/" className="btn btn-s">Explore API Documentation</Link>
            </div>
          </div>
          <figure className="business-api-visual">
            <img src={platformImage} alt="HAWKTRION WhatsApp platform for campaigns, customer messaging, CRM, and automation" />
            <figcaption>Connect WhatsApp campaigns to customer data and business workflows.</figcaption>
          </figure>
        </div>
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Customer Engagement" title="WhatsApp Marketing Made for Modern Businesses">
          WhatsApp is more than a messaging channel. It can be part of your customer acquisition, engagement, and retention strategy. Connect campaigns with your CRM, website, ecommerce platform, application, or internal systems.
        </SectionHeading>
        <p className="business-api-copy">Create targeted workflows, use approved message templates, personalize customer communication, and monitor message activity through API-powered integrations.</p>
        <h3 className="business-api-subheading">Build Marketing Workflows For</h3>
        <BulletList items={marketingWorkflows} />
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Platform Features" title="Powerful WhatsApp Marketing Features" />
        <CardGrid items={apiFeatures} />
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Personalization" title="Create More Relevant WhatsApp Campaigns">
          Use customer and business data from connected systems to make communication more useful. Instead of sending the same generic message to everyone, build around the customer&apos;s context.
        </SectionHeading>
        <BulletList items={personalizationFields} />
        <p className="business-api-copy">Use customer information only as permitted by your business policies, applicable laws, and WhatsApp requirements.</p>
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Templates" title="WhatsApp Message Templates">
          Use message templates for business-initiated communications where templates are required. Design appropriate variables so your application can populate customer-specific information dynamically.
        </SectionHeading>
        <BulletList items={templateUseCases} />
        <Link to="/whatsapp-api-documentation/" className="business-api-text-link">Read the API documentation</Link>
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Rich Messaging" title="Multimedia WhatsApp Marketing">
          Make campaigns more engaging with relevant content using media supported by the applicable WhatsApp message type.
        </SectionHeading>
        <BulletList items={mediaTypes} />
        <p className="business-api-copy">Use product images, promotional materials, documents, announcements, and other relevant content as part of your communication strategy.</p>
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Automation" title="Automate Your WhatsApp Marketing">
          Connect WhatsApp communication with business workflows using APIs and webhooks instead of manually sending every customer message.
        </SectionHeading>
        <Workflow steps={['Customer submits a lead form', 'Your application receives the lead', 'HAWKTRION API triggers WhatsApp communication', 'Customer receives a personalized message', 'Customer responds on WhatsApp', 'Webhook returns the event to your application', 'CRM or workflow continues the customer journey']} />
        <p className="business-api-copy">Make WhatsApp part of your existing business automation, not a separate manual process.</p>
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Lead Generation" title="WhatsApp Marketing for Lead Generation">
          Connect website forms, landing pages, advertisements, QR codes, and other customer touchpoints to WhatsApp workflows.
        </SectionHeading>
        <Workflow steps={['Website visitor', 'Lead form', 'WhatsApp follow-up', 'Sales conversation', 'CRM update', 'Personalized follow-up', 'Conversion']} />
        <p className="business-api-copy">Help sales teams respond quickly while keeping customer communication connected to existing systems.</p>
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="E-commerce" title="WhatsApp Marketing for E-commerce">
          Connect WhatsApp messaging with ecommerce customer journeys and trigger relevant communication from existing systems.
        </SectionHeading>
        <BulletList items={ecommerceJourneys} />
        <p className="business-api-copy">Connect your ecommerce application to the HAWKTRION API and trigger permitted messages from business events.</p>
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Customer Lifecycle" title="WhatsApp Marketing for Customer Engagement" />
        <CardGrid items={engagementSegments} />
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="SaaS Platforms" title="WhatsApp Marketing for SaaS Products">
          Integrate WhatsApp capabilities into your SaaS application instead of building a separate messaging system from scratch.
        </SectionHeading>
        <BulletList items={saasFeatures} />
        <p className="business-api-copy">Build WhatsApp messaging directly into the user experience of your product.</p>
        <Link to="/whatsapp-api-for-saas/" className="business-api-text-link">Explore WhatsApp API for SaaS</Link>
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Agencies" title="WhatsApp Marketing for Agencies">
          Agencies can connect client messaging requirements to existing systems and build client-specific workflows.
        </SectionHeading>
        <BulletList items={agencyUseCases} />
        <Link to="/whatsapp-api-for-agencies/" className="business-api-text-link">Explore WhatsApp API for Agencies</Link>
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Marketing Automation" title="Create Automated Customer Journeys" />
        <Workflow steps={['Trigger: customer performs an action', 'Condition: application evaluates event data', 'Message: generate relevant WhatsApp communication', 'Delivery: send through WhatsApp API', 'Webhook: receive delivery or interaction events', 'Next action: continue the workflow based on the result']} />
        <p className="business-api-copy">Use your existing technology stack to create structured customer journeys.</p>
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Integrations" title="Connect WhatsApp With Your Existing Technology">
          Use APIs and webhooks to exchange information between business systems and WhatsApp workflows.
        </SectionHeading>
        <BulletList items={applicationIntegrations} />
        <Link to="/whatsapp-api-integration/" className="business-api-text-link">Explore WhatsApp API Integration</Link>
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Developer API" title="WhatsApp Marketing API for Developers">
          Developers can integrate WhatsApp communication into applications using the HAWKTRION API.
        </SectionHeading>
        <BulletList items={developerCapabilities} />
        <a href="https://docs-waba.hawktrion.com/" target="_blank" rel="noopener noreferrer" className="business-api-text-link">View WhatsApp API Documentation</a>
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Measurement" title="WhatsApp Marketing Analytics">
          Connect messaging events and analytics with your business systems to understand how communication performs.
        </SectionHeading>
        <BulletList items={analyticsMetrics} />
        <p className="business-api-copy">Use available insights to improve customer journeys and optimize your communication strategy.</p>
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Customer Journeys" title="Build Better Customer Journeys With WhatsApp">
          Successful WhatsApp communication is about sending the right message to the right customer at the right stage of the journey.
        </SectionHeading>
        <CardGrid items={journeyStages} />
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Why HAWKTRION" title="Why Use an API-Based WhatsApp Marketing Platform?" />
        <CardGrid items={platformBenefits} />
      </div>
    </section>

    <section className="business-api-section">
      <div className="container">
        <SectionHeading eyebrow="Use Cases" title="WhatsApp Marketing Use Cases" />
        <CardGrid items={useCases} />
      </div>
    </section>

    <section className="business-api-section marketing-api-policy">
      <div className="container">
        <SectionHeading eyebrow="Customer Trust" title="Permission and Messaging Policies Matter">
          Use appropriate customer consent, follow applicable WhatsApp policies and laws, use approved templates where required, and respect customer opt-out requests. HAWKTRION tools do not replace your business&apos;s responsibility to follow messaging rules.
        </SectionHeading>
      </div>
    </section>

    <section className="business-api-section business-api-faq" id="whatsapp-marketing-faq">
      <div className="container">
        <SectionHeading eyebrow="Answers" title="WhatsApp Marketing FAQs" />
        <div className="business-api-faq-list">
          {faqs.map(([question, answer]) => <details key={question}><summary>{question}</summary><p>{answer}</p></details>)}
        </div>
      </div>
    </section>

    <section className="business-api-section business-api-final-cta">
      <div className="container">
        <div className="business-api-cta-panel">
          <SectionHeading eyebrow="Connect. Automate. Personalize. Engage." title="Build Your WhatsApp Marketing Infrastructure With HAWKTRION">
            Build WhatsApp-powered customer journeys with an API platform designed for businesses, SaaS companies, agencies, and developers.
          </SectionHeading>
          <div className="business-api-actions">
            <Link to="/signup" className="btn btn-p">Get Started</Link>
            <Link to="/whatsapp-api-documentation/" className="btn btn-s">Read the API Documentation</Link>
          </div>
        </div>
       
      </div>
    </section>
  </div>
);

export default WhatsappMarketingContent;